import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { seoAutopilotState, seoRunAutopilot } from "@/lib/seo/seo-os.functions";
import { SeoPage, Stat, Panel, Pill, Loading, Empty, num } from "@/components/admin/seo/SeoUi";
import { Button } from "@/components/ui/button";
import { Loader2, Play } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/seo/autopilot")({
  head: () => ({ meta: [{ title: "Autopilot · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: AutopilotPage,
});

function AutopilotPage() {
  const load = useServerFn(seoAutopilotState);
  const run = useServerFn(seoRunAutopilot);
  const q = useQuery({ queryKey: ["seo-autopilot-state"], queryFn: () => load() });
  const start = useMutation({
    mutationFn: () => run(),
    onSuccess: (res: any) => {
      toast.success(`Run ${res?.status}: ${res?.created ?? 0} suggestions created`);
      q.refetch();
    },
    onError: (e: any) => toast.error(e?.message ?? "Run failed"),
  });

  const d = q.data as any;

  return (
    <SeoPage
      title="Autopilot"
      description="Runs daily: collects the latest search data, works out where we can realistically gain, and prepares specific improvements. It never changes anything on its own unless you have allowed that risk level."
      actions={
        <Button size="sm" onClick={() => start.mutate()} disabled={start.isPending}>
          {start.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
          Run now
        </Button>
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : (
        <div className="grid gap-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat
              label="Automatic changes"
              value={d?.policy?.kill_switch ? "Paused" : "Allowed"}
              sub={d?.policy?.kill_switch ? "Everything waits for approval" : "Low-risk technical fixes only"}
            />
            <Stat label="Waiting for approval" value={num(d?.pending?.length ?? 0)} />
            <Stat label="Max changes per run" value={num(d?.policy?.max_actions_per_run ?? 0)} />
          </div>

          <Panel title="Run history">
            {d?.runs?.length ? (
              <div className="grid gap-2">
                {d.runs.map((r: any) => (
                  <div key={r.id} className="rounded-xl border border-border p-3 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-medium">{new Date(r.started_at).toLocaleString()}</span>
                      <Pill tone={r.status === "completed" ? "good" : r.status === "error" ? "bad" : "warn"}>{r.status}</Pill>
                    </div>
                    <p className="mt-1 text-muted-foreground">
                      {r.actions_created ?? 0} suggested · {r.actions_applied ?? 0} applied · {r.actions_blocked ?? 0} blocked
                      {r.error ? ` · ${r.error}` : ""}
                    </p>
                    {Array.isArray(r.stage_log) && r.stage_log.length ? (
                      <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                        {r.stage_log.map((s: any, i: number) => (
                          <li key={i}>
                            <span className="font-semibold">{s.stage}:</span> {s.detail}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <Empty>No runs yet.</Empty>
            )}
          </Panel>
        </div>
      )}
    </SeoPage>
  );
}
